import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceDot } from 'recharts';
import { getRequest } from '../../services/apiService';
import { Dialog } from 'primereact/dialog';
import * as saveSvgAsPng from 'save-svg-as-png';
import { Calendar, ChevronLeft, Briefcase, CheckSquare, AlertCircle, Clock3, MoreHorizontal, Filter, Download, FileText, Eye, Image, Layout, ChevronDown, X, Maximize2, Minimize2, Upload, Plus, ScrollText, Ship } from 'lucide-react';

const ProjectProgressSCurve = ({ selectedProject, selectedShip }) => {
  const [selectedModel, setSelectedModel] = useState("Logistic");
  const [showAllCharts, setShowAllCharts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const chartRef = useRef(null);

  const [allModelsDataCache, setAllModelsDataCache] = useState({});

  const availableModels = ['Logistic', 'Gompertz', 'Richards', 'Weibull', 'Bass'];

  const shapeSVG = {
    diamond: (color) => <polygon points="0,-6 6,0 0,6 -6,0" fill={color} />,
    circle: (color) => <circle r={6} fill={color} />,
    triangle: (color) => <polygon points="0,-7 7,7 -7,7" fill={color} />,
    rect: (color) => <rect x={-6} y={-6} width={12} height={12} fill={color} />,
    star: (color) => <text fill={color} fontSize={14} fontWeight="bold" dy={5} textAnchor="middle">★</text>,
    hexagon: (color) => <polygon points="6,0 3,5 -3,5 -6,0 -3,-5 3,-5" fill={color} />,
    cross: (color) => (
      <>
        <rect x={-1.5} y={-6} width={3} height={12} fill={color} />
        <rect x={-6} y={-1.5} width={12} height={3} fill={color} />
      </>
    ),
  };

  useEffect(() => {
    if (!selectedShip) {
      console.log("selectedShip is not defined. Skipping data fetch.");
      return;
    }

    const fetchModelData = async (modelName) => {
      console.log(`fetchModelData called for Ship ID: ${selectedShip}, Model: ${modelName}`);
      if (allModelsDataCache[modelName]) {
        console.log(`Data for model ${modelName} is already cached. Skipping API call.`);
        return;
      }
      setLoading(true);
      try {
        const apiUrl = `/transaction/api/milestone-progress/${selectedShip}/?models=${modelName}`;
        console.log(`Making API call for Ship ID: ${selectedShip}, Model: ${modelName}, API: ${apiUrl}`);
        const response = await getRequest(apiUrl);
        setAllModelsDataCache(prev => ({
          ...prev,
          [modelName]: response || { data: [], dynamicMilestones: [] }
        }));
        console.log(`API response for Ship ID: ${selectedShip}, Model: ${modelName}:`, response);
      } catch (error) {
        console.error(`Error fetching data for ${modelName}:`, error);
        setAllModelsDataCache(prev => ({
          ...prev,
          [modelName]: { data: [], dynamicMilestones: [] }
        }));
      } finally {
        setLoading(false);
      }
    };

    if (showAllCharts) {
      console.log("Fetching all charts mode.");
      availableModels.forEach(model => fetchModelData(model));
    } else {
      console.log(`Fetching single chart mode for model: ${selectedModel}`);
      fetchModelData(selectedModel);
    }
  }, [selectedShip, selectedModel, showAllCharts, availableModels, allModelsDataCache]);


  const getModelChartData = (model) => {
    return allModelsDataCache[model]?.data || [];
  };

  const getModelMilestones = (model) => {
    return allModelsDataCache[model]?.dynamicMilestones || [];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const planned = payload.find((p) => p.dataKey === "planned")?.value || 0;
      const projection = payload.find((p) => p.dataKey === "projection")?.value || 0;
      const actual = payload.find((p) => p.dataKey === "actual")?.value;
      const displayValue = actual !== undefined && actual !== null ? actual : projection;
      const diffValue = planned - displayValue;

      let milestoneInfo = '';
      if (payload[0]?.payload) {
        const modelNameForMilestones = showAllCharts ? payload[0]?.name : selectedModel;
        const milestones = getModelMilestones(modelNameForMilestones);
        
        // Find if there's a milestone with a 'percent' close to the current projection at 'label' (month)
        const currentDataPoint = getModelChartData(modelNameForMilestones).find(d => d.month === label);
        if (currentDataPoint) {
            const milestoneAtMonth = milestones.find(m => {
                // Check if the milestone's target percent is close to the current month's projection
                return Math.abs(m.percent - currentDataPoint.projection) < 0.5; // Tolerance for matching
            });
            if (milestoneAtMonth) {
                milestoneInfo = `Milestone: ${milestoneAtMonth.label} (${milestoneAtMonth.percent}%)`;
                if (milestoneAtMonth.date) {
                    milestoneInfo += ` - Date: ${milestoneAtMonth.date}`;
                }
            }
        }
      }

      return (
        <div style={{ background: "#fff", padding: 10, border: "1px solid #ccc" }}>
          <p><strong>Month: {label}</strong></p>
          <p>Planned: {planned.toFixed(2)}%</p>
          <p>{actual !== undefined && actual !== null ? "Actual: " : "Projection: "} {displayValue.toFixed(2)}%</p>
          <p>Difference: {diffValue.toFixed(2)}%</p>
          {milestoneInfo && <p style={{ color: 'blue' }}>{milestoneInfo}</p>}
        </div>
      );
    }
    return null;
  };

  const handleDownloadCSV = () => {
    let dataToDownload = [];
    const modelsToDownload = showAllCharts ? availableModels : [selectedModel];

    modelsToDownload.forEach(model => {
      const modelData = getModelChartData(model);
      const modelMilestones = getModelMilestones(model);
      
      modelData.forEach(item => {
        dataToDownload.push({
          model,
          month: item.month,
          planned: item.planned ?? '',
          actual: item.actual ?? '',
          projection: item.projection ?? '',
          averageDifference: item.averageDifference ?? '',
          type: 'Data Point'
        });
      });

      modelMilestones.forEach(milestone => {
        dataToDownload.push({
          model,
          month: '',
          planned: '',
          actual: '',
          projection: '',
          averageDifference: '',
          type: 'Milestone',
          milestoneLabel: milestone.label,
          milestoneDate: milestone.date,
          milestonePercent: milestone.percent
        });
      });
    });

    const csvContent = [
      ['Model', 'Type', 'Month', 'Planned (%)', 'Actual (%)', 'Projection (%)', 'Difference (%)', 'Milestone Label', 'Milestone Date', 'Milestone Percent'].join(','),
      ...dataToDownload.map(item => [
        item.model,
        item.type,
        item.month,
        item.planned,
        item.actual,
        item.projection,
        item.averageDifference,
        item.milestoneLabel || '',
        item.milestoneDate || '',
        item.milestonePercent || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `s-curve-data_${showAllCharts ? 'all_models' : selectedModel}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPNG = () => {
    const chartContainer = chartRef.current?.querySelector('.recharts-wrapper');
    if (chartContainer) {
      saveSvgAsPng.saveSvgAsPng(
        chartContainer.querySelector('svg'),
        `s-curve_${showAllCharts ? 'all_models' : selectedModel}.png`,
        {
          scale: 2,
          backgroundColor: '#ffffff'
        }
      );
    }
  };

  const renderDetailsTable = () => {
    const models = showAllCharts ? availableModels : [selectedModel];
    const data = models.flatMap(model => {
      const chartData = getModelChartData(model).map(item => ({ ...item, model, type: 'Data Point' }));
      const milestones = getModelMilestones(model).map(m => ({
        model,
        type: 'Milestone',
        label: m.label,
        date: m.date,
        percent: m.percent
      }));
      return [...chartData, ...milestones];
    });

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Planned (%)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual (%)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Projection (%)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difference (%)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Milestone Label</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Milestone Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Milestone Percent</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.model}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type === 'Data Point' ? item.month : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type === 'Data Point' ? (item.planned ?? '-') : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type === 'Data Point' ? (item.actual ?? '-') : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type === 'Data Point' ? (item.projection ?? '-') : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type === 'Data Point' ? (item.averageDifference ?? '-') : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type === 'Milestone' ? (item.label ?? '-') : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type === 'Milestone' ? (item.date ?? '-') : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type === 'Milestone' ? (item.percent ?? '-') : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSCurveChart = (model) => {
    const selectedData = getModelChartData(model);
    const apiMilestones = getModelMilestones(model);

    const mappedMilestones = apiMilestones.map(m => {
      let closestMonth = selectedData.length > 0 ? selectedData[0].month : 1;
      let minDiff = Infinity;

      // Find the month in selectedData that has a projection percentage closest to the milestone's target percent
      selectedData.forEach(d => {
        const diff = Math.abs(d.projection - m.percent);
        if (diff < minDiff) {
          minDiff = diff;
          closestMonth = d.month;
        }
      });

      return {
        label: m.label,
        color: m.color || "#000",
        shape: m.shape || "circle", // Use shape from API if available, else default to circle
        month: closestMonth, // The calculated month based on closest projection
        value: m.percent, // The actual percentage from the API milestone
        originalDate: m.date
      };
    });

    return (
      <div key={model}>
        {showAllCharts && <h3 className="text-lg font-semibold text-center mb-4">{model} Model</h3>}
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={selectedData} margin={{ top: 20, right: 40, left: 20, bottom: 40 }}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              stroke="#374151"
              label={{ value: "MONTH", position: "insideBottom", offset: -10, fill: "#374151", fontSize: 14 }}
              type="number"
            />
            <YAxis
              domain={[0, 100]}
              stroke="#374151"
              label={{ value: "PHYSICAL PROGRESS %", angle: -90, position: "insideLeft", fill: "#374151", fontSize: 14 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="top" height={36} />
            <Line type="monotone" dataKey="planned" stroke="#22223b" strokeWidth={2} dot={false} name="Cum Planned" />
            <Line type="monotone" dataKey="projection" stroke="#64748b" strokeDasharray="5 5" strokeWidth={2} dot={false} name="Avg. Progress Projection" />
            <Line type="monotone" dataKey="actual" stroke="#22c55e" strokeWidth={3} dot={false} name="Cumm Actual" />
            
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {mappedMilestones.map((m, idx) => (
            <div key={`milestone-detail-${model}-${idx}`} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
              <svg width="24" height="24" viewBox="-12 -12 24 24">
                {shapeSVG[m.shape || 'circle'](m.color)}
              </svg>
              <div>
                <div className="text-sm font-medium text-gray-900">{m.label}</div>
                <div className="text-xs text-gray-500">Month (approx): {m.month}</div>
                <div className="text-xs text-gray-500">Progress: {m.value}%</div>
                {m.originalDate && <div className="text-xs text-gray-500">Date: {m.originalDate}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg px-3 py-2" ref={chartRef}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          {!showAllCharts && (
            <div className="flex items-center gap-2">
              <label htmlFor="model-select" className="text-sm font-medium text-gray-700">Models:</label>
              <select
                id="model-select"
                value={selectedModel}
                onChange={(e) => {
                  const newModel = e.target.value;
                  console.log(`Dropdown changed. Setting selectedModel to: ${newModel}`);
                  setSelectedModel(newModel);
                }}
                className="form-select rounded-lg border border-gray-300 text-sm w-32"
              >
                {availableModels.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={() => setShowAllCharts(!showAllCharts)}
            className={`flex items-center gap-2 px-5 py-2 rounded-full font-semibold shadow-md bg-gradient-to-r from-indigo-500 to-indigo-700 text-white text-base transition-all duration-200 hover:from-indigo-400 hover:to-indigo-600 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-300`}
          >
            {showAllCharts ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                <span className="whitespace-nowrap">Single View</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-auto" viewBox="0 0 20 20" fill="currentColor">
                  <circle cx="5" cy="5" r="2" />
                  <circle cx="15" cy="5" r="2" />
                  <circle cx="5" cy="15" r="2" />
                  <circle cx="15" cy="15" r="2" />
                </svg>
                <span className="whitespace-nowrap">All Charts</span>
              </>
            )}
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDetails(true)}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
          >
            <Eye size={16} className="mr-1" />
            View Details
          </button>
          <button
            onClick={handleDownloadCSV}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
            title="Download CSV"
          >
            <FileText size={16} className="mr-1" />
            CSV
          </button>
          <button
            onClick={handleDownloadPNG}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
            title="Download PNG"
          >
            <Image size={16} className="mr-1" />
            PNG
          </button>
        </div>
      </div>

      <Dialog
        visible={showDetails}
        onHide={() => setShowDetails(false)}
        header="S-Curve Detailed Data"
        className="w-full max-w-7xl"
        draggable={false}
      >
        {renderDetailsTable()}
      </Dialog>

      {loading && Object.keys(allModelsDataCache).length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500">Loading S-Curve data...</div>
      ) : showAllCharts ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {availableModels.map(model => (
            <div key={model}>
              {allModelsDataCache[model] ? renderSCurveChart(model) : <div className="flex items-center justify-center h-48 text-gray-400">Loading or no data for {model}...</div>}
            </div>
          ))}
        </div>
      ) : (
        allModelsDataCache[selectedModel] ? renderSCurveChart(selectedModel) : <div className="flex items-center justify-center h-64 text-gray-500">No data available for {selectedModel} model.</div>
      )}
    </div>
  );
};

export default ProjectProgressSCurve;